import React, { useState, useRef, useEffect } from 'react';
import {
    Search,
    Filter,
    Download,
    ChevronDown,
    ChevronUp,
    Users,
    UserPlus,
    Maximize2,
    Minimize2,
    Plus,
    Minus,
    X,
    ClipboardList,
    Briefcase,
    Calendar
} from 'lucide-react';

const PositionStructure = () => {
    const [viewAs, setViewAs] = useState('Admin');
    const [detailedView, setDetailedView] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedNodes, setExpandedNodes] = useState({});
    const [zoomLevel, setZoomLevel] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [showDownloadMenu, setShowDownloadMenu] = useState(false);
    const [highlightedNode, setHighlightedNode] = useState(null);
    const [selectedFilters, setSelectedFilters] = useState({
        jobOccupancyStatus: [],
        department: [],
        role: [],
        position: [],
        reportingManager: [],
        band: []
    });
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [showRequisitionModal, setShowRequisitionModal] = useState(false);
    const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);

    const fullscreenRef = useRef(null);
    const nodeRefs = useRef({});
    const scrollContainerRef = useRef(null);
    const contentRef = useRef(null);

    // Position Structure Data
    const positionData = {
        company: 'Relentless',
        rootPosition: {
            id: 'pos1',
            code: 'POS1',
            title: 'Legal Head',
            department: 'CORPORATE FINANCE, LEGAL & TAXATION DIRECTORATE',
            status: 'occupied', // 'open', 'need-to-hire', 'occupied'
            directReports: 2,
            totalReportees: 7,
            children: [
                {
                    id: 'pos2',
                    code: 'POS2',
                    title: 'Senior Manager',
                    department: 'CORPORATE FINANCE, LEGAL & TAXATION DIRECTORATE',
                    status: 'hybrid', // Has both open and need-to-hire
                    directReports: 2,
                    totalReportees: 2,
                    openPositions: 2,
                    needToHire: 2,
                    children: [
                        {
                            id: 'pos7',
                            code: 'POS7',
                            title: 'Manager',
                            department: 'CORPORATE FINANCE, LEGAL & TAXATION DIRECTORATE',
                            status: 'hybrid',
                            directReports: 0,
                            totalReportees: 0,
                            openPositions: 1,
                            needToHire: 1
                        }
                    ]
                },
                {
                    id: 'pos3',
                    code: 'POS3',
                    title: 'Senior Manager',
                    department: 'CORPORATE FINANCE, LEGAL & TAXATION DIRECTORATE',
                    status: 'hybrid',
                    directReports: 0,
                    totalReportees: 0,
                    openPositions: 1,
                    needToHire: 1
                },
                {
                    id: 'pos4',
                    code: 'POS4',
                    title: 'Accountant',
                    department: 'FINANCE DIVISION',
                    status: 'hybrid',
                    directReports: 0,
                    totalReportees: 0,
                    openPositions: 1,
                    needToHire: 1
                },
                {
                    id: 'pos5',
                    code: 'POS5',
                    title: 'B99_HRBP',
                    department: 'CORPORATE FINANCE, LEGAL & TAXATION DIRECTORATE',
                    status: 'hybrid',
                    directReports: 0,
                    totalReportees: 0,
                    openPositions: 1,
                    needToHire: 1
                }
            ]
        }
    };

    // Toggle node expansion
    const toggleExpand = (nodeId) => {
        setExpandedNodes(prev => ({
            ...prev,
            [nodeId]: !prev[nodeId]
        }));
    };

    // Handle position click - removed to prevent expanding on card click
    const handlePositionClick = (position) => {
        setSelectedPosition(position);
        // Removed auto-expand - only chevron should expand
    };

    // Filter logic - Check if position matches selected filters
    const isPositionVisible = (position) => {
        // If no filters selected, show all
        const hasFilters = Object.values(selectedFilters).some(arr => arr.length > 0);
        if (!hasFilters) return true;

        // Check job occupancy status
        if (selectedFilters.jobOccupancyStatus.length > 0) {
            let statusMatch = false;
            if (position.status === 'occupied' && selectedFilters.jobOccupancyStatus.includes('Occupied Positions')) {
                statusMatch = true;
            }
            if (position.status === 'open' && selectedFilters.jobOccupancyStatus.includes('Open Positions')) {
                statusMatch = true;
            }
            if (position.status === 'need-to-hire' && selectedFilters.jobOccupancyStatus.includes('Need to Hire')) {
                statusMatch = true;
            }
            if (position.status === 'hybrid') {
                if (position.openPositions > 0 && selectedFilters.jobOccupancyStatus.includes('Open Positions')) {
                    statusMatch = true;
                }
                if (position.needToHire > 0 && selectedFilters.jobOccupancyStatus.includes('Need to Hire')) {
                    statusMatch = true;
                }
            }
            if (!statusMatch) return false;
        }

        // Check department
        if (selectedFilters.department.length > 0) {
            const deptMatch = selectedFilters.department.some(dept =>
                position.department?.includes(dept.split(',')[0]) // Match partial department name
            );
            if (!deptMatch) return false;
        }

        // Check role
        if (selectedFilters.role.length > 0) {
            const roleMatch = selectedFilters.role.some(role =>
                position.title?.toLowerCase().includes(role.toLowerCase())
            );
            if (!roleMatch) return false;
        }

        // Check position code
        if (selectedFilters.position.length > 0) {
            const posMatch = selectedFilters.position.some(pos =>
                pos.includes(position.code)
            );
            if (!posMatch) return false;
        }

        return true;
    };

    // Apply filters function
    const applyFilters = () => {
        setShowFilters(false);
        // Filters are applied automatically through isPositionVisible function
    };

    // Handle zoom
    const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.1, 2));
    const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.1, 0.5));

    // Handle fullscreen
    const handleFullscreen = () => {
        if (!isFullscreen) {
            fullscreenRef.current?.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
        setIsFullscreen(!isFullscreen);
    };

    // Search functionality - Improved with better scrolling
    const handleSearch = () => {
        if (!searchQuery.trim()) {
            setHighlightedNode(null);
            return;
        }

        const searchTerm = searchQuery.toLowerCase().trim();
        let foundNodeId = null;

        // Recursive search function
        const searchNode = (node, expandPath = {}) => {
            // Check if any field contains the search term (partial match)
            if (node.title.toLowerCase().includes(searchTerm) ||
                node.code.toLowerCase().includes(searchTerm) ||
                node.department.toLowerCase().includes(searchTerm)) {
                foundNodeId = node.id;
                return expandPath;
            }

            if (node.children) {
                for (let child of node.children) {
                    const newPath = { ...expandPath, [node.id]: true };
                    const result = searchNode(child, newPath);
                    if (foundNodeId) return result;
                }
            }

            return expandPath;
        };

        const expandPath = searchNode(positionData.rootPosition);

        if (foundNodeId) {
            // Expand necessary nodes to show the found item
            setExpandedNodes(prev => ({ ...prev, ...expandPath }));
            setHighlightedNode(foundNodeId);

            // Improved scrolling with both horizontal and vertical
            setTimeout(() => {
                if (nodeRefs.current[foundNodeId] && scrollContainerRef.current) {
                    const element = nodeRefs.current[foundNodeId];

                    // Use scrollIntoView for better cross-browser support
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'center'
                    });

                    // Additional focus effect
                    element.classList.add('ring-4', 'ring-blue-500', 'ring-opacity-50');
                    setTimeout(() => {
                        element.classList.remove('ring-4', 'ring-blue-500', 'ring-opacity-50');
                    }, 3000);
                }
            }, 800); // Increased delay to ensure expansion completes
        } else {
            setHighlightedNode(null);
            alert('No matching position found');
        }
    };

    // Get status colors and indicators
    const getStatusColors = (status) => {
        switch (status) {
            case 'occupied':
                return { border: 'border-green-500', bg: 'bg-green-50', indicator: 'bg-green-500' };
            case 'open':
                return { border: 'border-orange-500', bg: 'bg-orange-50', indicator: 'bg-orange-500' };
            case 'need-to-hire':
                return { border: 'border-red-500', bg: 'bg-red-50', indicator: 'bg-red-500' };
            case 'hybrid':
                return { border: 'border-gray-300', bg: 'bg-white', indicator: null };
            default:
                return { border: 'border-gray-300', bg: 'bg-white', indicator: 'bg-gray-300' };
        }
    };

    // Position Card Component
    const PositionCard = ({ position, isChild = false }) => {
        const isExpanded = expandedNodes[position.id];
        const isHighlighted = highlightedNode === position.id;
        const statusColors = getStatusColors(position.status);

        // Check if this position should be visible based on filters
        if (!isPositionVisible(position)) {
            return null;
        }

        // Filter visible children
        const visibleChildren = position.children?.filter(child => isPositionVisible(child)) || [];
        const hasVisibleChildren = visibleChildren.length > 0;
        const handleChevronClick = (e, positionId) => {
            e.stopPropagation();
            toggleExpand(positionId);
        };
        return (
            <div className="flex flex-col items-center">
                {isChild && (
                    <div className="w-0.5 h-12 bg-gray-300" />
                )}

                <div
                    ref={el => nodeRefs.current[position.id] = el}
                    onClick={() => handlePositionClick(position)}
                    className={`relative bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-4 w-72 cursor-pointer transition-all border-2 ${
                        statusColors.border
                    } ${
                        isHighlighted ? 'ring-4 ring-blue-400' : ''
                    } ${position.status === 'occupied' ? 'bg-green-50 dark:bg-green-900/20' : ''}`}
                >
                    {/* Status Indicators - Made smaller (3-4 char width) */}
                    <div className="flex gap-1 mb-2">
                        {position.status === 'hybrid' && (
                            <>
                                {position.openPositions > 0 && (
                                    <div className="h-0.5 bg-orange-500" style={{ width: '16px' }} />
                                )}
                                {position.needToHire > 0 && (
                                    <div className="h-0.5 bg-red-500" style={{ width: '16px' }} />
                                )}
                            </>
                        )}
                        {position.status === 'open' && (
                            <div className="h-0.5 bg-orange-500" style={{ width: '32px' }} />
                        )}
                        {position.status === 'need-to-hire' && (
                            <div className="h-0.5 bg-red-500" style={{ width: '32px' }} />
                        )}
                        {position.status === 'occupied' && (
                            <div className="h-0.5 bg-green-500" style={{ width: '32px' }} />
                        )}
                    </div>

                    <div className="flex items-start justify-between mb-2">
                        <div>
                            <h3 className="font-semibold text-blue-600 dark:text-blue-400 underline cursor-pointer">
                                {position.code}
                            </h3>
                            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{position.title}</p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedPosition(position);
                                    setShowAddEmployeeModal(true);
                                }}
                                className="p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                title="Add New Employee"
                            >
                                <UserPlus size={16} />
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedPosition(position);
                                    setShowRequisitionModal(true);
                                }}
                                className="p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                title="Raise Requisition"
                            >
                                <ClipboardList size={16} />
                            </button>
                        </div>
                    </div>

                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                        {position.department}
                    </p>

                    {/* Reports Information */}
                    {position.directReports !== undefined && (
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex gap-4 text-xs">
                                {position.status === 'hybrid' && position.openPositions && (
                                    <span className="text-orange-600 dark:text-orange-400">{position.openPositions}</span>
                                )}
                                {position.status === 'hybrid' && position.needToHire && (
                                    <span className="text-red-600 dark:text-red-400">{position.needToHire}</span>
                                )}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                                {position.directReports > 0 && (
                                    <span>{position.directReports} Direct | {position.totalReportees} Total Reportees</span>
                                )}
                                {hasVisibleChildren && (
                                    <button
                                        onClick={(e) => handleChevronClick(e, position.id)}
                                        className="ml-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                    >
                                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {isExpanded && hasVisibleChildren && (
                    <div className="flex flex-col items-center">
                        <div className="w-0.5 h-12 bg-gray-300" />

                        {visibleChildren.length === 1 ? (
                            <div className="flex">
                                {visibleChildren.map(child => (
                                    <PositionCard key={child.id} position={child} isChild={true} />
                                ))}
                            </div>
                        ) : (
                            <>
                                <div className="h-0.5 bg-gray-300" style={{
                                    width: `${300 * visibleChildren.length}px`
                                }} />
                                <div className="flex gap-8" style={{ marginTop: '-2px' }}>
                                    {visibleChildren.map(child => (
                                        <PositionCard key={child.id} position={child} isChild={true} />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Download Menu Component
    const DownloadMenu = () => (
        <div className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl dark:shadow-gray-900 border border-gray-200 dark:border-gray-700 z-20">
            <button className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 text-sm text-gray-900 dark:text-gray-100">Download Image</button>
            <button className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 text-sm text-gray-900 dark:text-gray-100">Download PPT</button>
            <button className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 text-sm text-gray-900 dark:text-gray-100">Download PDF</button>
        </div>
    );

    // Initialize with root expanded
    useEffect(() => {
        setExpandedNodes({ [positionData.rootPosition.id]: true });
    }, []);

    // Handle detailed view toggle - Auto expand all when ON
    useEffect(() => {
        if (detailedView) {
            const allNodeIds = {};

            const expandAll = (node) => {
                if (node.children && node.children.length > 0) {
                    allNodeIds[node.id] = true;
                    node.children.forEach(expandAll);
                }
            };

            expandAll(positionData.rootPosition);
            setExpandedNodes(allNodeIds);
        } else {
            // Optionally collapse all when turned OFF
            setExpandedNodes({ [positionData.rootPosition.id]: true });
        }
    }, [detailedView]);

    return (
        <div ref={fullscreenRef} className={`min-h-screen bg-gray-100 dark:bg-gray-900 ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
            {/* Header */}
            {!isFullscreen && (
                <div className="bg-white dark:bg-gray-800 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <select
                                value={viewAs}
                                onChange={(e) => setViewAs(e.target.value)}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option>View As: Admin</option>
                                <option>View As: Manager</option>
                                <option>View As: Employee</option>
                            </select>

                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    className="pl-4 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm w-64 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                                <button
                                    onClick={handleSearch}
                                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer"
                                >
                                    <Search size={16} />
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => setShowFilters(true)}
                                className="flex items-center space-x-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
                            >
                                <Filter size={16} />
                                <span className="text-sm">Filters</span>
                                {Object.values(selectedFilters).some(arr => arr.length > 0) && (
                                    <span className="ml-1 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                                        {Object.values(selectedFilters).reduce((sum, arr) => sum + arr.length, 0)}
                                    </span>
                                )}
                            </button>

                            <div className="relative">
                                {/*<button*/}
                                {/*    onClick={() => setShowDownloadMenu(!showDownloadMenu)}*/}
                                {/*    className="flex items-center space-x-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"*/}
                                {/*>*/}
                                {/*    <Download size={16} />*/}
                                {/*    <span className="text-sm">Download</span>*/}
                                {/*    <ChevronDown size={16} />*/}
                                {/*</button>*/}
                                {showDownloadMenu && <DownloadMenu />}
                            </div>

                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Detailed View</span>
                                <button
                                    onClick={() => setDetailedView(!detailedView)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                        detailedView ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                                    }`}
                                >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        detailedView ? 'translate-x-6' : 'translate-x-1'
                                    }`} />
                                </button>
                            </div>

                            <div className="flex items-center space-x-1 border-l border-gray-300 dark:border-gray-600 pl-4">
                                <button onClick={handleZoomOut} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                                    <Minus className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                </button>
                                <button onClick={handleZoomIn} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                                    <Plus className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                </button>
                                <button onClick={handleFullscreen} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                                    <Maximize2 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Legend */}
                    <div className="flex items-center space-x-6 mt-4">
                        <div className="flex items-center space-x-2">
                            <div className="w-4 h-1 bg-orange-500"></div>
                            <span className="text-sm text-gray-600">Open Positions</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-4 h-1 bg-red-500"></div>
                            <span className="text-sm text-gray-600">Need to Hire</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-4 h-1 bg-green-500"></div>
                            <span className="text-sm text-gray-600">Occupied Positions</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <div
                ref={scrollContainerRef}
                className="overflow-auto"
                style={{
                    height: isFullscreen ? '100vh' : 'calc(100vh - 150px)',
                    position: 'relative'
                }}
            >
                <div
                    ref={contentRef}
                    style={{
                        transform: `scale(${zoomLevel})`,
                        transformOrigin: 'top left',
                        padding: '32px',
                        minWidth: 'max-content',
                        display: 'inline-block',
                        position: 'relative'
                    }}
                >
                    <div className="flex flex-col items-center">
                        {/* Company Name */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl dark:shadow-gray-900 px-6 py-3 mb-12">
                            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">{positionData.company}</h2>
                        </div>

                        {/* Root Position and Hierarchy */}
                        <PositionCard position={positionData.rootPosition} />
                    </div>
                </div>
            </div>

            {/* Filter Panel */}
            {showFilters && (
                <div className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-gray-800 shadow-2xl z-50 overflow-y-auto">
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Filter by</h2>
                        <button onClick={() => setShowFilters(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                            <X size={20} className="text-gray-700 dark:text-gray-300" />
                        </button>
                    </div>

                    <div className="p-6 space-y-6">
                        {/* Job Occupancy Status */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Job Occupancy Status</h3>
                            <div className="space-y-2">
                                {['Open Positions', 'Need to Hire', 'Occupied Positions'].map(status => (
                                    <label key={status} className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={selectedFilters.jobOccupancyStatus.includes(status)}
                                            onChange={() => {
                                                setSelectedFilters(prev => ({
                                                    ...prev,
                                                    jobOccupancyStatus: prev.jobOccupancyStatus.includes(status)
                                                        ? prev.jobOccupancyStatus.filter(s => s !== status)
                                                        : [...prev.jobOccupancyStatus, status]
                                                }));
                                            }}
                                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                                        />
                                        <span className="text-sm text-gray-700 dark:text-gray-300">{status}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Department */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-3">Department</h3>
                            <div className="space-y-2">
                                {[
                                    'CORPORATE FINANCE, LEGAL & TAXATION',
                                    'FINANCE DIVISION',
                                    'HUMAN RESOURCES',
                                    'INFORMATION TECHNOLOGY',
                                    'SALES & MARKETING'
                                ].map(dept => (
                                    <label key={dept} className="flex items-start space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={selectedFilters.department.includes(dept)}
                                            onChange={() => {
                                                setSelectedFilters(prev => ({
                                                    ...prev,
                                                    department: prev.department.includes(dept)
                                                        ? prev.department.filter(d => d !== dept)
                                                        : [...prev.department, dept]
                                                }));
                                            }}
                                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 mt-0.5"
                                        />
                                        <span className="text-sm text-gray-700">{dept}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Role */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-3">Role</h3>
                            <div className="space-y-2">
                                {[
                                    'Legal Head',
                                    'Senior Manager',
                                    'Manager',
                                    'Assistant Manager',
                                    'Accountant',
                                    'Executive',
                                    'HRBP'
                                ].map(role => (
                                    <label key={role} className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={selectedFilters.role.includes(role)}
                                            onChange={() => {
                                                setSelectedFilters(prev => ({
                                                    ...prev,
                                                    role: prev.role.includes(role)
                                                        ? prev.role.filter(r => r !== role)
                                                        : [...prev.role, role]
                                                }));
                                            }}
                                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">{role}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Position */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-3">Position</h3>
                            <div className="space-y-2">
                                {[
                                    'POS1 - Legal Head',
                                    'POS2 - Senior Manager',
                                    'POS3 - Senior Manager',
                                    'POS4 - Accountant',
                                    'POS5 - B99_HRBP',
                                    'POS7 - Manager'
                                ].map(pos => (
                                    <label key={pos} className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={selectedFilters.position.includes(pos)}
                                            onChange={() => {
                                                setSelectedFilters(prev => ({
                                                    ...prev,
                                                    position: prev.position.includes(pos)
                                                        ? prev.position.filter(p => p !== pos)
                                                        : [...prev.position, pos]
                                                }));
                                            }}
                                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">{pos}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Reporting Manager */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-3">Reporting Manager</h3>
                            <div className="space-y-2">
                                {[
                                    'John Smith',
                                    'Jane Doe',
                                    'Robert Johnson',
                                    'Emily Williams',
                                    'Michael Brown'
                                ].map(manager => (
                                    <label key={manager} className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={selectedFilters.reportingManager.includes(manager)}
                                            onChange={() => {
                                                setSelectedFilters(prev => ({
                                                    ...prev,
                                                    reportingManager: prev.reportingManager.includes(manager)
                                                        ? prev.reportingManager.filter(m => m !== manager)
                                                        : [...prev.reportingManager, manager]
                                                }));
                                            }}
                                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">{manager}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Band */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-3">Band</h3>
                            <div className="space-y-2">
                                {[
                                    'Senior Management',
                                    'Middle Management',
                                    'Junior Management',
                                    'Executive Level',
                                    'Entry Level'
                                ].map(band => (
                                    <label key={band} className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={selectedFilters.band.includes(band)}
                                            onChange={() => {
                                                setSelectedFilters(prev => ({
                                                    ...prev,
                                                    band: prev.band.includes(band)
                                                        ? prev.band.filter(b => b !== band)
                                                        : [...prev.band, band]
                                                }));
                                            }}
                                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">{band}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <button
                            onClick={() => {
                                setSelectedFilters({
                                    jobOccupancyStatus: [],
                                    department: [],
                                    role: [],
                                    position: [],
                                    reportingManager: [],
                                    band: []
                                });
                            }}
                            className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                        >
                            Clear All
                        </button>
                        <button
                            onClick={() => {
                                applyFilters();
                            }}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            APPLY
                        </button>
                    </div>
                </div>
            )}
            {isFullscreen && (
                <div className="absolute top-4 right-4 flex items-center space-x-2">
                    <div className="flex items-center space-x-4 bg-white p-2 rounded-lg shadow-lg">
                        <div className="flex items-center space-x-2">
                            <div className="w-4 h-1 bg-orange-500"></div>
                            <span className="text-xs text-gray-600">Open</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-4 h-1 bg-red-500"></div>
                            <span className="text-xs text-gray-600">Need to Hire</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-4 h-1 bg-green-500"></div>
                            <span className="text-xs text-gray-600">Occupied</span>
                        </div>
                    </div>
                    <button onClick={handleZoomOut} className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-100">
                        <Minus className="w-4 h-4 text-gray-600" />
                    </button>
                    <span className="px-2 py-1 bg-white rounded-lg shadow-lg text-sm text-gray-700">
                        {Math.round(zoomLevel * 100)}%
                    </span>
                    <button onClick={handleZoomIn} className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-100">
                        <Plus className="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                        onClick={handleFullscreen}
                        className="p-2 bg-blue-500 text-white rounded-lg shadow-lg hover:bg-blue-600 ml-2"
                    >
                        <Minimize2 className="w-5 h-5" />
                    </button>
                </div>
            )}

            {/* Raise Requisition Modal */}
            {showRequisitionModal && selectedPosition && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Raise a Requisition</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Company *
                                </label>
                                <input
                                    type="text"
                                    value={positionData.company}
                                    disabled
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Department *
                                </label>
                                <input
                                    type="text"
                                    value={selectedPosition.department}
                                    disabled
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Role *
                                </label>
                                <input
                                    type="text"
                                    value={`${selectedPosition.title} (${selectedPosition.code})`}
                                    disabled
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Band
                                </label>
                                <input
                                    type="text"
                                    value="Middle Management"
                                    disabled
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Grade
                                </label>
                                <input
                                    type="text"
                                    value="B1"
                                    disabled
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-4 mt-6">
                            <button
                                onClick={() => setShowRequisitionModal(false)}
                                className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                CANCEL
                            </button>
                            <button
                                onClick={() => {
                                    alert('Requisition submitted successfully!');
                                    setShowRequisitionModal(false);
                                }}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                SUBMIT
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Employee Modal */}
            {showAddEmployeeModal && selectedPosition && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Add Employee</h2>

                        <div className="space-y-8">
                            {/* Personal Details Section */}
                            <div>
                                <h3 className="font-medium text-gray-900 mb-4 pb-2 border-b">Personal Details</h3>

                                {/* Name Fields */}
                                <div className="grid grid-cols-3 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            First Name *
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Middle Name
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Last Name *
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>

                                {/* Date of Birth and Personal Email */}
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Date of Birth *
                                        </label>
                                        <input
                                            type="date"
                                            placeholder="DD/MM/YYYY"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Personal Email ID
                                        </label>
                                        <input
                                            type="email"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>

                                {/* Contact Information and Marital Status */}
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Contact Information
                                        </label>
                                        <div className="flex gap-2">
                                            <select className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                                                <option>+1</option>
                                                <option>+91</option>
                                                <option>+44</option>
                                                <option>+86</option>
                                            </select>
                                            <input
                                                type="tel"
                                                placeholder="Phone number"
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Marital Status
                                        </label>
                                        <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                                            <option>-Select-</option>
                                            <option>Single</option>
                                            <option>Married</option>
                                            <option>Do not like to disclose</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Date of Joining */}
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Date of Joining *
                                        </label>
                                        <input
                                            type="date"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Employment Details Section */}
                            <div>
                                <h3 className="font-medium text-gray-900 mb-4 pb-2 border-b">Employment Details</h3>

                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Department *
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Search"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Role *
                                        </label>
                                        <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                                            <option>Senior Manager</option>
                                            <option>Manager</option>
                                            <option>Assistant Manager</option>
                                            <option>Team Lead</option>
                                            <option>Executive</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Select Position *
                                        </label>
                                        <select
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            defaultValue={`${selectedPosition.title} (${selectedPosition.code})`}
                                        >
                                            <option>{selectedPosition.title} ({selectedPosition.code})</option>
                                            <option>Senior Manager 1 Legal (POS2)</option>
                                            <option>Senior Manager 2 Legal (POS3)</option>
                                            <option>Accountant Finance (POS4)</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Reporting Manager *
                                        </label>
                                        <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                                            <option>John Smith</option>
                                            <option>Jane Doe</option>
                                            <option>Robert Johnson</option>
                                            <option>Emily Williams</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Employment Type
                                        </label>
                                        <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                                            <option>Permanent</option>
                                            <option>Temporary</option>
                                            <option>Contractual</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Work Location *
                                        </label>
                                        <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                                            <option>New York Office</option>
                                            <option>London Office</option>
                                            <option>Mumbai Office</option>
                                            <option>Singapore Office</option>
                                            <option>Remote</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Other Information
                                    </label>
                                    <textarea
                                        rows="3"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Any additional information..."
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-4 mt-8 pt-4 border-t">
                            <button
                                onClick={() => setShowAddEmployeeModal(false)}
                                className="px-8 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                            >
                                CANCEL
                            </button>
                            <button
                                onClick={() => {
                                    alert('Employee added successfully!');
                                    setShowAddEmployeeModal(false);
                                }}
                                className="px-8 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                            >
                                ADD
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PositionStructure;